import { useEffect, useRef } from "react";
import { Circle, Group } from "react-konva";
import type Konva from "konva";
import type {
  SharedArrowShape,
  SharedLineShape,
} from "@/lib/canvas/shared-shape";
import { pointToPx, type Dims } from "@/lib/canvas/coords";
import { withEndpoint, type EndpointEnd } from "@/lib/canvas/shape-edit";

/**
 * Draggable endpoint anchors for a SELECTED arrow/line — the
 * sanctioned exception to "resize goes through Konva.Transformer"
 * (a Transformer scales a bounding box, which is the wrong gesture
 * for repositioning one end of a two-point shape).
 *
 * Konva-owns-paint: during an anchor drag the underlying Arrow/Line
 * node (looked up by `#${shape.id}` — `ShapeRenderer` sets the id on
 * the root node) is updated via `node.points()` directly; React/store
 * state is written exactly once, on drag-end, via `onCommit`.
 *
 * Abort semantics: unmounting mid-drag (Escape deselects, Delete
 * removes the shape) must CANCEL, not commit — Konva's `destroy()`
 * calls `stopDrag()` which still fires `dragend`, so the handler
 * checks an unmounting ref (set in the effect cleanup, which React
 * runs before destroying host nodes) and restores the line instead
 * of committing. A drag that would produce a degenerate (<0.005)
 * shape is likewise restored, mirroring the draw-path rejection.
 *
 * Both editors render these as the LAST children of the shapes layer
 * (after the Transformer) so the anchors sit on top of the hit graph
 * and an anchor press always beats the underlying draggable line.
 * While the LINE ITSELF drags (drag-start auto-select can mount the
 * handles mid-gesture), the anchors hide Konva-side so they don't
 * float at the pre-drag endpoints.
 */

const HANDLE_VISIBLE_RADIUS = 6;
/** Invisible hit circle — sized for touch. */
const HANDLE_HIT_RADIUS = 14;
/** Matches the shared selected-amber used by every renderer. */
const HANDLE_STROKE = "#fbbf24";
/** Same threshold the draw paths use to reject degenerate shapes. */
const DEGENERATE_EPSILON = 0.005;

export type EndpointHandlesProps = {
  shape: SharedArrowShape | SharedLineShape;
  dims: Dims;
  /** One store write per completed anchor drag (one undo snapshot). */
  onCommit: (next: SharedArrowShape | SharedLineShape) => void;
};

export function EndpointHandles(props: EndpointHandlesProps) {
  const { shape, dims, onCommit } = props;
  const startRef = useRef<Konva.Group | null>(null);
  const endRef = useRef<Konva.Group | null>(null);
  // Set during the effect cleanup — which React runs BEFORE the Konva
  // nodes are destroyed — so a destroy-triggered dragend can tell an
  // aborted gesture from a completed one.
  const unmountingRef = useRef(false);

  useEffect(() => {
    unmountingRef.current = false;
    return () => {
      unmountingRef.current = true;
    };
  }, []);

  // Hide the anchors while the underlying line drags as a whole —
  // drag-start auto-select mounts the handles mid-gesture, and they
  // would otherwise float at the stale pre-drag endpoints until the
  // commit re-render.
  useEffect(() => {
    const line = startRef.current?.getStage()?.findOne(`#${shape.id}`);
    if (!line) return;
    const setVisible = (v: boolean) => {
      startRef.current?.visible(v);
      endRef.current?.visible(v);
      startRef.current?.getLayer()?.batchDraw();
    };
    if (line.isDragging()) setVisible(false);
    line.on("dragstart.endpoint-handles", () => setVisible(false));
    line.on("dragend.endpoint-handles", () => setVisible(true));
    return () => {
      line.off(".endpoint-handles");
    };
  });

  if (!shape.id) return null;

  const start = pointToPx({ x: shape.x1, y: shape.y1 }, dims);
  const end = pointToPx({ x: shape.x2, y: shape.y2 }, dims);

  const findLine = (node: Konva.Node) =>
    node.getStage()?.findOne(`#${shape.id}`) as Konva.Line | undefined;

  // Live Konva-side repaint of the line while an anchor drags. The
  // non-dragged end comes from props, which are stable for the whole
  // gesture (no React writes until commit).
  const syncLine = (which: EndpointEnd, node: Konva.Node) => {
    const line = findLine(node);
    if (!line) return;
    const p = { x: node.x(), y: node.y() };
    line.points(
      which === "start"
        ? [p.x, p.y, end.x, end.y]
        : [start.x, start.y, p.x, p.y],
    );
    line.getLayer()?.batchDraw();
  };

  // Aborted / rejected gesture: put the line and the anchor back to
  // the pre-drag props positions instead of committing.
  const restore = (which: EndpointEnd, node: Konva.Node) => {
    const line = findLine(node);
    line?.points([start.x, start.y, end.x, end.y]);
    node.position(which === "start" ? start : end);
    line?.getLayer()?.batchDraw();
  };

  const renderHandle = (
    which: EndpointEnd,
    at: { x: number; y: number },
    ref: React.MutableRefObject<Konva.Group | null>,
  ) => (
    <Group
      // Callback ref — react-konva's LegacyRef typing rejects a
      // nullable RefObject directly.
      ref={(node) => {
        ref.current = node;
      }}
      name="endpoint-handle"
      x={at.x}
      y={at.y}
      draggable
      // Clamp to the stage so the committed unit coords always equal
      // the node's final position — keeps the post-commit re-render
      // idempotent (no snap-back).
      dragBoundFunc={(pos) => ({
        x: Math.max(0, Math.min(dims.width, pos.x)),
        y: Math.max(0, Math.min(dims.height, pos.y)),
      })}
      onDragMove={(e) => syncLine(which, e.target)}
      onDragEnd={(e) => {
        const next = withEndpoint(shape, which, {
          x: e.target.x() / dims.width,
          y: e.target.y() / dims.height,
        });
        const degenerate =
          Math.abs(next.x1 - next.x2) < DEGENERATE_EPSILON &&
          Math.abs(next.y1 - next.y2) < DEGENERATE_EPSILON;
        if (unmountingRef.current || degenerate) {
          restore(which, e.target);
          return;
        }
        onCommit(next);
      }}
    >
      <Circle radius={HANDLE_HIT_RADIUS} opacity={0} />
      <Circle
        radius={HANDLE_VISIBLE_RADIUS}
        fill="#ffffff"
        stroke={HANDLE_STROKE}
        strokeWidth={2}
        listening={false}
      />
    </Group>
  );

  return (
    <>
      {renderHandle("start", start, startRef)}
      {renderHandle("end", end, endRef)}
    </>
  );
}
